import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { BabyProfile, convertBabyToProfile } from "@/types/baby";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isPremium: boolean;
  subscriptionEnd: string | null;
  checkSubscription: () => Promise<void>;
  signOut: () => Promise<{ error: any | null }>;
  selectedBaby: BabyProfile | null;
  setSelectedBaby: (baby: BabyProfile | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPremium, setIsPremium] = useState(false);
  const [subscriptionEnd, setSubscriptionEnd] = useState<string | null>(null);
  const [selectedBaby, setSelectedBaby] = useState<BabyProfile | null>(null);

  const updateSelectedBaby = useCallback((baby: BabyProfile | null) => {
    setSelectedBaby(baby);
    if (baby) {
      localStorage.setItem('selectedBabyId', baby.id);
    } else {
      localStorage.removeItem('selectedBabyId');
    }
  }, []);

  const checkSubscription = async (activeSession?: Session | null) => {
    const sessionToUse = activeSession ?? session;
    if (!sessionToUse?.access_token) {
      setIsPremium(false);
      setSubscriptionEnd(null);
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke("check-subscription", {
        headers: {
          Authorization: `Bearer ${sessionToUse.access_token}`,
        },
      });

      if (error) {
        setIsPremium(false);
        setSubscriptionEnd(null);
      } else {
        setIsPremium(data?.subscribed || false);
        setSubscriptionEnd(data?.subscription_end || null);
      }
    } catch (error) {
      console.error("Error calling check-subscription:", error);
      setIsPremium(false);
      setSubscriptionEnd(null);
    }
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (!error) {
      setUser(null);
      setSession(null);
      setIsPremium(false);
      setSubscriptionEnd(null);
      setSelectedBaby(null);
    }
    return { error };
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        setSession(newSession);
        setUser(newSession?.user ?? null);
        setIsLoading(false);

        if (newSession?.user) {
          setTimeout(() => {
            checkSubscription(newSession);
          }, 0);
        } else {
          setIsPremium(false);
          setSubscriptionEnd(null);
          setSelectedBaby(null);
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session: existingSession } }) => {
      setSession(existingSession);
      setUser(existingSession?.user ?? null);
      setIsLoading(false);

      if (existingSession?.user) {
        setTimeout(() => {
          checkSubscription(existingSession);
        }, 0);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Auto-refresh subscription status every 5 minutes (only in production)
  useEffect(() => {
    if (session && import.meta.env.PROD) {
      const interval = setInterval(checkSubscription, 300000);
      return () => clearInterval(interval);
    }
  }, [session]);

  const value = {
    user,
    session,
    isLoading,
    isPremium,
    subscriptionEnd,
    checkSubscription,
    signOut,
    selectedBaby,
    setSelectedBaby: updateSelectedBaby
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};